// Taşeron portalı — Belge Analizi gelen kutusu: sol raydaki 3 seviyeli ağacın saf (React'sız)
// gruplama mantığı (plan S25 §Aşama C, satır C3/C3b; `docs/plans/S25-analysis-inbox.md`).
// Seviye 1 (analiz) çağıran tarafta (`AnalysisInboxRail`/`DocumentAnalysisInboxPage`) yaşar —
// bu dosya yalnız TEK bir analizin kalemlerini 2. seviye gruplara ayırır.
//
// **Üyelik kuralı (C3b) — her kalemin TAM OLARAK bir evi vardır:**
//   1) `item.subjectId !== null` VE o id `subjects[]`'te varsa → o kişinin grubu.
//   2) `subjectId === null` VE `entity === 'company'` → `Firma belgeleri`.
//   3) Yukarıdakilerin hiçbiri değilse (`subjectId === null` ve firma belgesi değil —
//      tanınmayan ve `vehicle` kalemler dahil, #17'ye kadar ayrı bir araç grubu yok —, YA DA
//      `subjectId` dolu ama `subjects[]`'te karşılığı yok — iki bağımsız anlık görüntü
//      arasında beklenmeyen bir sapma) → `Kişi belirlenemedi`. Bir kalem asla sessizce
//      kaybolmaz.
//
// Gruplama BİZZAT `item.subjectId` üzerinden yapılır, `subjects[].itemUuids`'e GÜVENİLMEZ —
// ikisi sözleşmede kardeş dizilerdir (`DocumentAnalysisDetailDto`), tek bir kaynaktan
// TÜRETİLDİĞİ vaat edilmez.

import type {
  DocumentAnalysisDetailDto,
  DocumentAnalysisItemDto,
  DocumentAnalysisSubjectDto,
  DocumentEntity,
} from '@isgportal/shared';
import type { TFunction } from 'i18next';

export type AnalysisGroupKind = 'subject' | 'company' | 'unresolved';

export interface AnalysisGroup {
  kind: AnalysisGroupKind;
  /** Kararlı React key + seçim eşleştirmesi için. */
  key: string;
  /** Yalnız `kind === 'subject'` iken dolu. */
  subject: DocumentAnalysisSubjectDto | null;
  items: DocumentAnalysisItemDto[];
}

export type AnalysisItemStatus = 'pending' | 'accepted' | 'rejected';

/** Bir kalemin üç görünür durumundan hangisi (AC3) — kalemin KENDİ alanlarından türetilir,
 * ayrı bir istemci bayrağı YOK (sayfa yenilemesinden sonra da doğru kalır). */
export function itemStatus(item: DocumentAnalysisItemDto): AnalysisItemStatus {
  if (item.rejectedAt !== null) {
    return 'rejected';
  }
  if (item.acceptedLinkUuid !== null) {
    return 'accepted';
  }
  return 'pending';
}

/** Bir analizin kalemlerini C3b kuralına göre 2. seviye gruplara ayırır. Boş grup ÜRETMEZ —
 * sıfır kalemli bir kişi/kova ekranda dangling bir başlık olarak KALMAZ. */
export function groupAnalysisItems(detail: DocumentAnalysisDetailDto): AnalysisGroup[] {
  const subjectsById = new Map(detail.subjects.map((s) => [s.subjectId, s]));
  const itemsBySubjectId = new Map<string, DocumentAnalysisItemDto[]>();
  const companyItems: DocumentAnalysisItemDto[] = [];
  const unresolvedItems: DocumentAnalysisItemDto[] = [];

  for (const item of detail.items) {
    if (item.subjectId !== null && subjectsById.has(item.subjectId)) {
      const list = itemsBySubjectId.get(item.subjectId);
      if (list) {
        list.push(item);
      } else {
        itemsBySubjectId.set(item.subjectId, [item]);
      }
      continue;
    }
    if (item.subjectId === null && item.entity === 'company') {
      companyItems.push(item);
    } else {
      unresolvedItems.push(item);
    }
  }

  const groups: AnalysisGroup[] = [];
  for (const subject of detail.subjects) {
    const items = itemsBySubjectId.get(subject.subjectId) ?? [];
    if (items.length > 0) {
      groups.push({ kind: 'subject', key: `subject:${subject.subjectId}`, subject, items });
    }
  }
  if (companyItems.length > 0) {
    groups.push({ kind: 'company', key: 'company', subject: null, items: companyItems });
  }
  if (unresolvedItems.length > 0) {
    groups.push({ kind: 'unresolved', key: 'unresolved', subject: null, items: unresolvedItems });
  }
  return groups;
}

/** 2. seviye başlığın görüntü metni — tek yerde (rail + inceleme paneli aynı adı okur). */
export function analysisGroupLabel(group: AnalysisGroup, t: TFunction): string {
  if (group.kind === 'subject') {
    return group.subject?.fullName ?? t('analysis.result.subjectUnnamed');
  }
  if (group.kind === 'company') {
    return t('analysis.inbox.companyGroupHeading');
  }
  return t('analysis.inbox.unresolvedGroupHeading');
}

/** Kayıttan etiket çözücü — `useDocumentTypes().label` (kayıtta yoksa ham kod döner). */
export type DocumentTypeLabelFn = (entity: DocumentEntity, code: string) => string;

/**
 * Bir kalemin görüntü adı — rail satırı, rail araması ve inceleme panelinin `h2`'si AYNI metni
 * okur (tek yer). `(entity, code)` §27 platform kaydından çözülür (api-contract §20 — sunucu etiket
 * göndermez; GL #7): kayıtta olmayan ya da kayıt henüz yüklenmemişken kod HAM basılır, sessizce
 * kaybolmaz; tanınmayan kalem (`entity === null`) tek bir anahtara düşer.
 */
export function analysisItemLabel(
  item: DocumentAnalysisItemDto,
  t: TFunction,
  label: DocumentTypeLabelFn,
): string {
  // Kabul edilmiş kalem: insan onaylı `acceptedType` (§20.1, bağlayıcı karar) modelin önerisinin
  // (`entity`/`code`) önüne geçer — tanınmayan kalem şirkete kabul edilince de gerçek adını alır.
  if (item.acceptedTarget !== null && item.acceptedType !== null) {
    return label(item.acceptedTarget, item.acceptedType);
  }
  if (item.entity === null) {
    return t('analysis.item.unrecognised');
  }
  return label(item.entity, item.code);
}

/** Üstteki `X/Y karar verildi` göstergesi — reddedilmiş kalemler de "karar verildi" sayılır
 * (yalnız kabul edilenler DEĞİL); `detail.items` S25'ten beri reddedilmiş kalemleri de
 * İÇERİR (bkz. `documentAnalyses.ts` dosya üstü notu), bu yüzden ayrı bir sorgu gerekmez. */
export function countDecisions(items: DocumentAnalysisItemDto[]): {
  decided: number;
  total: number;
} {
  let decided = 0;
  for (const item of items) {
    if (item.rejectedAt !== null || item.acceptedLinkUuid !== null) {
      decided += 1;
    }
  }
  return { decided, total: items.length };
}

/**
 * `findNextPendingItem`in giriş şekli — `AnalysisInboxRail.tsx`'in `AnalysisInboxRailEntry`'sine
 * YAPISAL olarak uyar (o tipi buradan İTHAL ETMEZ — rail zaten bu dosyayı import ediyor,
 * döngüsel import kurulmasın diye sadece gereken alanlar burada yeniden tanımlanır).
 */
export interface AnalysisTraversalEntry {
  analysis: { uuid: string };
  detail: DocumentAnalysisDetailDto | undefined;
}

/**
 * Karardan SONRA odağın taşınacağı bir sonraki bekleyen belge (plan C12/AC16, tasarım D10 —
 * inceleme panelinin footer'ındaki `Onayla`ya DEĞİL). Ray'in KENDİ render sırasıyla (analiz →
 * grup → kalem) aynı sırada düzleştirir, karar VERİLEN kalemin konumundan bir sonrakinden
 * başlayıp SARARAK (wrap-around) arar.
 *
 * `currentItemUuid` kendisi ASLA aday olarak seçilmez — çağıran taraf bunu mutasyonun
 * `onSuccess`'inde, sorgu ÖNBELLEĞİ henüz tazelenmeden (senkron) çağırabilir; o anda kalemin
 * `itemStatus`'ü hâlâ `pending` görünüyor olabilir. Bu koruma o yarışı yok sayar — sonuç doğru
 * kalır, yalnız `invalidateQueries`in tamamlanmasını BEKLEMEK gerekmez.
 *
 * Bekleyen kalem yoksa `null` (panel "hepsi karara bağlandı" boş durumuna düşer).
 */
export function findNextPendingItem(
  entries: AnalysisTraversalEntry[],
  currentAnalysisUuid: string | null,
  currentItemUuid: string | null,
): { analysisUuid: string; itemUuid: string } | null {
  const flat: { analysisUuid: string; item: DocumentAnalysisItemDto }[] = [];
  for (const entry of entries) {
    if (!entry.detail) {
      continue;
    }
    for (const group of groupAnalysisItems(entry.detail)) {
      for (const item of group.items) {
        flat.push({ analysisUuid: entry.analysis.uuid, item });
      }
    }
  }
  const total = flat.length;
  if (total === 0) {
    return null;
  }
  const currentIndex = flat.findIndex(
    (f) => f.analysisUuid === currentAnalysisUuid && f.item.uuid === currentItemUuid,
  );
  const start = currentIndex === -1 ? 0 : currentIndex + 1;
  for (let step = 0; step < total; step += 1) {
    const candidate = flat[(start + step) % total];
    if (!candidate) {
      continue;
    }
    const isCurrent =
      candidate.analysisUuid === currentAnalysisUuid && candidate.item.uuid === currentItemUuid;
    if (!isCurrent && itemStatus(candidate.item) === 'pending') {
      return { analysisUuid: candidate.analysisUuid, itemUuid: candidate.item.uuid };
    }
  }
  return null;
}
